import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { enableMapSet } from "immer";
import axiosClient from "../../axios-client";
import { toast } from "react-toastify";

enableMapSet();

export const getAllImages = createAsyncThunk(
  "images/getAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`image/getAllImageExplorer`, { params });
      return {
        data: response.data.data,
        totalGridData: response.data.total,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch data"
      );
    }
  }
);

export const getEnlargeImage = createAsyncThunk(
  "images/getEnlargeImage",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`image/getEnlargeVendorImage`, {
        params,
      });
      return {
        enlargeImageData: response.data.data,
        total: response.data.total,
        pageId: response.data.pageId,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch enlarged image data"
      );
    }
  }
);

export const getEnlargeOtherImage = createAsyncThunk(
  "images/getEnlargeOtherImage",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`image/getEnlargeOtherImage`, {
        params,
      });
      return {
        enlargeImageData: response.data.data,
        total: response.data.total,
        pageId: response.data.pageId,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch enlarged image data"
      );
    }
  }
);

export const updateImageStatus = createAsyncThunk(
  "images/updateStatus",
  async (payload, { rejectWithValue }) => {
    try {
      // Use the entire payload directly as the request body
      const response = await axiosClient.post(
        `image/UpdateStatusMultipleImage`,
        payload // Send the full payload as JSON body
      );

      if (response.data.statusCode === 200) {
        // Return submitted data so reducer can update immediately
        return { ...response.data, ...payload }; // Include the original payload in the return
      } else {
        toast.error(response.data.message || "Failed to update image status");
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      toast.error("Error updating image status");
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const likeImage = createAsyncThunk(
  "images/likeImage",
  async ({ imageId, customerId, likeStatus }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`image/imageLike`, {
        likeStatus,
        imageId,
        customerId,
      });
      return {
        ...response.data.data,
        imageId,
        likeStatus: response.data.message === "Image liked" ? 1 : 0,
        total_likes: response.data.data.total_likes || 0,
      };
    } catch (error) {
      toast.error("Error liking image");
      return rejectWithValue(error.message);
    }
  }
);

export const imageClick = createAsyncThunk(
  "images/imageClick",
  async ({ imageId, userId }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`image/imageClick`, {
        imageId,
        userId,
      });
      return response.data;
    } catch (error) {
      toast.error("Error recording image click");
      return rejectWithValue(
        error.response?.data?.message || "Failed to record image click"
      );
    }
  }
);

export const boostUpdateStatus = createAsyncThunk(
  "images/boostUpdateStatus",
  async ({ imageId, request }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(
        `image/BoostUpdateStatus/${imageId}`,
        request
      );
      if (response.data.statusCode === 200) {
        return { imageId, request, response: response.data };
      } else {
        toast.error(response.data.message || "Failed to update boost status");
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      toast.error("Error updating boost status");
      return rejectWithValue(error.message);
    }
  }
);

export const getImageScoreById = createAsyncThunk(
  "images/scoreDetailsByImageId",
  async ({ imageId }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `image/scoreDetailsByImageId/${imageId}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch score details"
      );
    }
  }
);

export const getReportListing = createAsyncThunk(
  "images/reportListing",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { pageId, pageSize, userId } = params;
      const response = await axiosClient.get("image/reportListing", {
        params: { pageId, pageSize, userId },
      });
      if (response.data?.statusCode === 200) {
        return {
          recordData: response.data.data,
          totalRecords: response.data.total,
          reportMessage: response.data.message,
          pageId: response.data.pageId,
          pageSize: response.data.pageSize,
          userId: response.data.userId,
        };
      } else {
        return rejectWithValue(
          toast.error(response.data?.message || "Something went wrong")
        );
      }
    } catch (error) {
      return rejectWithValue(
        toast.error(
          error.response?.data?.message || "Failed to fetch report listing"
        )
      );
    }
  }
);

export const updateReportStatus = createAsyncThunk(
  "images/updateReportStatus",
  async ({ reportId, reportStatus }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("image/reportStatusUpdate", {
        params: { reportId, reportStatus },
      });
      if (response.data?.statusCode === 200) {
        return {
          reportId,
          reportStatus,
          createdByEmail: response.data.createdByEmail,
          reportUpdateMessage: response.data.message,
        };
      } else {
        return rejectWithValue(
          toast.error(
            response.data?.message || "Failed to update report status"
          )
        );
      }
    } catch (error) {
      return rejectWithValue(
        toast.error(error.response?.data?.message || "Something went wrong")
      );
    }
  }
);

export const getImageLikeById = createAsyncThunk(
  "images/getImageLikeById",
  async ({ imageId }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`image/imagelikeById/${imageId}`);
      return { imageId, isLiked: response.data.is_liked === "1" };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch image like status"
      );
    }
  }
);

export const multipleImageBoost = createAsyncThunk(
  "images/multipleImageBoost",
  async ({ payload, isVideo }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `image/MultipleImageBoost`,
        payload
      );
      if (
        response.data.message === "Image boost records created successfully"
      ) {
        return { payload, isVideo, response: response.data };
      } else {
        toast.error(response.data.message || "Failed to boost media");
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      toast.error("Error boosting media");
      return rejectWithValue(error.message);
    }
  }
);

export const reportImageData = createAsyncThunk(
  "images/reportImageData",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/image/imageReport`, params);
      if (response.data.message === "Image report submitted") {
        return response.data;
      } else {
        return rejectWithValue(
          response.data.message || "Failed to submit report"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error submitting report"
      );
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  gridSearch: "",
  Inspired: 0,
  submitting: false,
  errorSubmit: null,
  imageData: [],
  finalImages: [],
  enlargeImageData: [],
  finalVideos: [],
  currentPage: 1,
  total: 0,
  pageId: 1,
  totalGridData: 0,
  reportLoading: false,
  reportError: null,
  scoreDetails: null,
  scoreStatus: "idle",
  loadedPages: [],
  pendingRequests: [],
  gridLoading: false,
  recordData: [],
  totalRecords: 0,
  reportMessage: "",
  pageSize: 10,
  userId: null,
  reportUpdateMessage: null,
  clickedImageId: "yes",
};

const imageSlice = createSlice({
  name: "image",
  initialState,
  reducers: {
    setReportData: (state, action) => {
      state.recordData = action.payload.recordData;
      state.totalRecords = action.payload.totalRecords;
      state.reportMessage = action.payload.reportMessage;
    },
    setFinalImages: (state, action) => {
      state.finalImages = action.payload;
    },
    setFinalVideos: (state, action) => {
      state.finalVideos = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    addLoadedPage: (state, action) => {
      if (!state.loadedPages.includes(action.payload)) {
        state.loadedPages.push(action.payload);
      }
    },
    setInspired: (state, action) => {
      state.Inspired = action.payload;
    },
    resetImageData: (state) => {
      state.imageData = [];
      state.currentPage = 1;
      state.total = 0;
      state.pageId = 1;
      state.loadedPages = [];
      state.pendingRequests = [];
    },
    addPendingRequest: (state, action) => {
      if (!state.pendingRequests.includes(action.payload)) {
        state.pendingRequests.push(action.payload);
      }
    },
    removePendingRequest: (state, action) => {
      state.pendingRequests = state.pendingRequests.filter(
        (request) => request !== action.payload
      );
    },
    setGridSearch: (state, action) => {
      state.gridSearch = action.payload;
    },
    setClickedImageId: (state, action) => {
      state.clickedImageId = action.payload;
    },
    resetEnlargeImageData: (state) => {
      state.enlargeImageData = [];
      state.total = 0;
      state.pageId = 1;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReportListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReportListing.fulfilled, (state, action) => {
        state.loading = false;
        state.recordData = action.payload.recordData.map((row) => ({
          ...row,
          status: row.reportStatus,
        }));
        state.totalRecords = action.payload.totalRecords;
        state.reportMessage = action.payload.reportMessage;
        state.pageId = action.payload.pageId;
        state.pageSize = action.payload.pageSize;
        state.userId = action.payload.userId;
      })
      .addCase(getReportListing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateReportStatus.pending, (state) => {
        state.reportLoading = true;
        state.reportError = null;
      })
      .addCase(updateReportStatus.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.reportUpdateMessage = action.payload.reportUpdateMessage;
        state.recordData = state.recordData.map((row) =>
          row.id === action.payload.reportId
            ? {
                ...row,
                status: action.payload.reportStatus,
                createdByEmail: action.payload.createdByEmail,
              }
            : row
        );
      })
      .addCase(updateReportStatus.rejected, (state, action) => {
        state.reportLoading = false;
        state.reportError = action.payload;
      })
      .addCase(reportImageData.pending, (state) => {
        state.reportLoading = true;
        state.reportError = null;
      })
      .addCase(reportImageData.fulfilled, (state, action) => {
        state.reportLoading = false;
      })
      .addCase(reportImageData.rejected, (state, action) => {
        state.reportLoading = false;
        state.reportError = action.payload;
      })
      .addCase(getImageLikeById.pending, (state) => {
        state.submitting = true;
        state.errorSubmit = null;
      })
      .addCase(getImageLikeById.fulfilled, (state, action) => {
        state.submitting = false;
        const { imageId, isLiked } = action.payload;
        state.enlargeImageData = state.enlargeImageData.map((image) =>
          image.id === imageId || image.image_id === imageId
            ? { ...image, is_liked: isLiked ? "1" : "0" }
            : image
        );
      })
      .addCase(getImageLikeById.rejected, (state, action) => {
        state.submitting = false;
        state.errorSubmit = action.payload;
      })
      .addCase(imageClick.pending, (state) => {
        state.submitting = true;
        state.errorSubmit = null;
      })
      .addCase(imageClick.fulfilled, (state, action) => {
        state.submitting = false;
      })
      .addCase(imageClick.rejected, (state, action) => {
        state.submitting = false;
        state.errorSubmit = action.payload;
      })
      .addCase(likeImage.pending, (state) => {
        state.submitting = true;
        state.errorSubmit = null;
      })
      .addCase(likeImage.fulfilled, (state, action) => {
        state.submitting = false;
        const { imageId, likeStatus, totalLikes } = action.payload;
        state.enlargeImageData = state.enlargeImageData.map((vendor) => ({
          ...vendor,
          images: vendor.images.map((image) =>
            image.image_id === imageId
              ? {
                  ...image,
                  is_liked: likeStatus === 1 ? "1" : "0",
                  total_likes: totalLikes.toString(),
                }
              : image
          ),
        }));
      })
      .addCase(likeImage.rejected, (state, action) => {
        state.submitting = false;
        state.errorSubmit = action.payload;
      })
      .addCase(getAllImages.pending, (state, action) => {
        const requestKey = JSON.stringify(action.meta.arg);
        if (!state.pendingRequests.includes(requestKey)) {
          state.gridLoading = true;
          state.pendingRequests.push(requestKey);
        }
      })
      .addCase(getAllImages.fulfilled, (state, action) => {
        state.gridLoading = false;
        const requestKey = JSON.stringify(action.meta.arg);
        state.pendingRequests = state.pendingRequests.filter(
          (request) => request !== requestKey
        );
        const pageId = action.meta.arg.pageId || 1;
        if (pageId === 1) {
          state.imageData = action.payload.data;
        } else {
          const existingIds = new Set(state.imageData.map((item) => item.id));
          const newData = action.payload.data.filter(
            (item) => !existingIds.has(item.id)
          );
          state.imageData = [...state.imageData, ...newData];
        }
        state.totalGridData = action.payload.totalGridData;
        state.pageId = pageId;
      })
      .addCase(getAllImages.rejected, (state, action) => {
        state.gridLoading = false;
        const requestKey = JSON.stringify(action.meta.arg);
        state.pendingRequests = state.pendingRequests.filter(
          (request) => request !== requestKey
        );
        state.error = action.payload;
      })
      .addCase(getEnlargeImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEnlargeImage.fulfilled, (state, action) => {
        state.loading = false;
        const newVendors = action.payload.enlargeImageData;
        const existingVendorIds = new Set(
          state.enlargeImageData.map((vendor) => vendor.vendorId)
        );
        const uniqueNewVendors = newVendors.filter(
          (vendor) => !existingVendorIds.has(vendor.vendorId)
        );
        state.enlargeImageData = [
          ...state.enlargeImageData,
          ...uniqueNewVendors,
        ];
        state.total = action.payload.total;
        state.pageId = action.payload.pageId;
      })
      .addCase(getEnlargeImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateImageStatus.fulfilled, (state, action) => {
        state.submitting = false;
        const { imageStatus, imageData } = action.payload; // Changed from ImageStatus to imageStatus

        try {
          imageData.forEach((group) => {
            const postTypeKey = Object.keys(group)[0];
            const postsArray = group[postTypeKey];
            postsArray.forEach((postObj) => {
              const postIdKey = Object.keys(postObj)[0];
              const imageIds = (postObj[postIdKey]?.imageIds || []).map(Number);

              // Update grid list
              state.imageData = state.imageData.map((image) =>
                imageIds.includes(image.id)
                  ? { ...image, image_status: imageStatus } // Changed from ImageStatus to imageStatus
                  : image
              );

              // Update enlarge list
              state.enlargeImageData = state.enlargeImageData.map((item) => {
                if (typeof item?.image_id === "number") {
                  return imageIds.includes(item.image_id)
                    ? { ...item, image_status: imageStatus } // Changed from ImageStatus to imageStatus
                    : item;
                }
                if (Array.isArray(item?.images)) {
                  return {
                    ...item,
                    images: item.images.map((img) =>
                      imageIds.includes(img.image_id)
                        ? { ...img, image_status: imageStatus } // Changed from ImageStatus to imageStatus
                        : img
                    ),
                  };
                }
                return item;
              });
            });
          });
        } catch {
          // Avoid crashing on unexpected structure
        }
      })
      .addCase(updateImageStatus.rejected, (state, action) => {
        state.submitting = false;
        state.errorSubmit = action.payload;
      })
      .addCase(boostUpdateStatus.pending, (state) => {
        state.submitting = true;
      })
      .addCase(boostUpdateStatus.fulfilled, (state, action) => {
        state.submitting = false;
        const { imageId, request } = action.payload;
        state.imageData = state.imageData.map((image) =>
          image.id === imageId
            ? {
                ...image,
                boost_status: request.boostStatus,
                boost_click_limit: request.adminClickLimit,
                boost_likes_limit: request.adminLikeLimit,
              }
            : image
        );
        state.enlargeImageData = state.enlargeImageData.map((image) =>
          image.image_id === imageId
            ? {
                ...image,
                boost_status: request.boostStatus,
                boost_click_limit: request.adminClickLimit,
                boost_likes_limit: request.adminLikeLimit,
              }
            : image
        );
      })
      .addCase(getEnlargeOtherImage.pending, (state) => {
        state.isLoadingMoreVendors = true;
      })
      .addCase(getEnlargeOtherImage.fulfilled, (state, action) => {
        state.isLoadingMoreVendors = false;
        state.loading = false;
        state.enlargeImageData = action.payload.enlargeImageData;
        state.total = action.payload.total;
        state.pageId = action.payload.pageId;
      })
      .addCase(getEnlargeOtherImage.rejected, (state, action) => {
        state.isLoadingMoreVendors = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(boostUpdateStatus.rejected, (state, action) => {
        state.submitting = false;
        state.errorSubmit = action.payload;
      })
      .addCase(multipleImageBoost.pending, (state) => {
        state.submitting = true;
        state.errorSubmit = null;
      })
      .addCase(multipleImageBoost.fulfilled, (state, action) => {
        state.submitting = false;
        const { payload, isVideo, response } = action.payload;
        const imageIds = payload.imageIds.split(",").map(Number);
        state.imageData = state.imageData.map((image) => {
          const boostData = response.data.find(
            (item) => item.imageId === image.id
          );
          if (imageIds.includes(image.id) && boostData) {
            return {
              ...image,
              boost: payload.Boost,
              boost_status: boostData.boostStatus,
              boost_click_limit: boostData.clickLimit,
              boost_likes_limit: boostData.likeLimit,
            };
          }
          return image;
        });
        state.enlargeImageData = state.enlargeImageData.map((image) => {
          const boostData = response.data.find(
            (item) => item.imageId === image.image_id
          );
          if (imageIds.includes(image.image_id) && boostData) {
            return {
              ...image,
              boost: payload.Boost,
              boost_status: boostData.boostStatus,
              boost_click_limit: boostData.clickLimit,
              boost_likes_limit: boostData.likeLimit,
            };
          }
          return image;
        });
        if (isVideo) {
          state.finalVideos = state.imageData
            .filter((item) => item.image_type === 1)
            .sort((a, b) => a.image_position - b.image_position);
        } else {
          state.finalImages = state.imageData
            .filter((item) => item.image_type === 0)
            .sort((a, b) => a.image_position - b.image_position);
        }
      })
      .addCase(multipleImageBoost.rejected, (state, action) => {
        state.submitting = false;
        state.errorSubmit = action.payload;
      })
      .addCase(getImageScoreById.pending, (state) => {
        state.scoreStatus = "loading";
        state.scoreDetails = null;
        state.error = null;
      })
      .addCase(getImageScoreById.fulfilled, (state, action) => {
        state.scoreStatus = "succeeded";
        state.scoreDetails = action.payload;
      })
      .addCase(getImageScoreById.rejected, (state, action) => {
        state.scoreStatus = "failed";
        state.error = action.payload;
      });
  },
});

export default imageSlice.reducer;

export const {
  setReportData,
  setFinalImages,
  setFinalVideos,
  setCurrentPage,
  setGridSearch,
  addLoadedPage,
  setInspired,
  addPendingRequest,
  removePendingRequest,
  resetImageData,
  setClickedImageId,
  resetEnlargeImageData,
} = imageSlice.actions;

export const getEnlargeImagesData = (params) => {
  return async (dispatch) => {
    await dispatch(
      getEnlargeImage({
        imageId: params.imageId,
        vendorId: params.createdBy || params.vendorId,
        userId: params.userId,
        imageClick: params.imageClick,
      })
    );
  };
};

export const getEnlargeOtherImagesData = (params) => {
  return async (dispatch) => {
    await dispatch(
      getEnlargeOtherImage({
        pageSize: params.pageSize,
        createdBy: params.createdBy,
        pageId: params.pageId,
        userId: params.userId,
      })
    );
  };
};

export const getImagesData = (params) => {
  return async (dispatch, getState) => {
    const state = getState();
    const requestKey = JSON.stringify(params);

    if (
      state.image.pendingRequests.includes(requestKey) ||
      state.image.gridLoading
    ) {
      return;
    }

    await dispatch(getAllImages(params));
  };
};

export const likeImageData = (params) => {
  return async (dispatch) => {
    await dispatch(
      likeImage({
        likeStatus: params.likeStatus,
        imageId: params.imageId,
        customerId: params.customerId,
      })
    );
  };
};

export const imageClickData = (params) => {
  return async (dispatch) => {
    await dispatch(
      imageClick({
        imageId: params.imageId,
        userId: params.userId,
      })
    );
  };
};

export const getImageLikeByIdData = (params) => {
  return async (dispatch) => {
    await dispatch(
      getImageLikeById({
        imageId: params.imageId,
      })
    );
  };
};

export const reportImage = (params) => {
  return async (dispatch) => {
    await dispatch(reportImageData(params));
  };
};

export const updateImageStatusData = (payload) => {
  return async (dispatch) => {
    await dispatch(updateImageStatus(payload));
  };
};
